import { JobWorldFilters } from "./job_world_filters.js"

const STORAGE_KEY = "devfoundJobResultsView"
const MOBILE_BREAKPOINT = 768

let bound = false
let cacheBound = false

function isDesktop() {
  return window.innerWidth >= MOBILE_BREAKPOINT
}

function hasWebGL() {
  try {
    const c = document.createElement("canvas")
    return !!(c.getContext("webgl2") || c.getContext("webgl"))
  } catch (_e) {
    return false
  }
}

function root() {
  return document.querySelector("[data-job-world-view-root]")
}

function readPayload(panel) {
  const tag = panel.querySelector("[data-job-world-view-data]")
  if (!tag) return []
  try {
    return JSON.parse(tag.textContent || "[]")
  } catch (_e) {
    return []
  }
}

// Treat the "/" homepage as the only place where the user's stored "world"
// preference is honored. Tag/region/company landing pages also render the
// home/index template, but on those pages the world-view payload is the
// global 90-day feed — *not* scoped to the page's subject — so opening it
// would be confusing (e.g., "I clicked Acme's pin, why am I seeing pins for
// every other company?"). Defaulting non-home routes to list view keeps
// each landing page focused on the jobs that actually belong to it.
function isHomepageRoute() {
  const p = window.location.pathname || "/"
  return p === "/" || p === ""
}

function storedMode() {
  try {
    return window.sessionStorage.getItem(STORAGE_KEY) || "list"
  } catch (_e) {
    return "list"
  }
}

function storeMode(mode) {
  // Only persist the user's choice when they're on the homepage. Saving on
  // a company / tag landing page would clobber the home preference and
  // surprise the user when they navigate back ("I had the globe open, why
  // is it list now?").
  if (!isHomepageRoute()) return
  try {
    window.sessionStorage.setItem(STORAGE_KEY, mode)
  } catch (_e) {
    // ignore (private browsing)
  }
}

function disableTopFilters(disabled) {
  const filters = document.querySelector(".filters-section-container")
  if (!filters) return
  filters.classList.toggle("is-world-view-active", disabled)
  filters.querySelectorAll("button, input, select").forEach((el) => {
    if (el.closest(".job-view-toggle")) return
    el.setAttribute("aria-disabled", String(disabled))
    if (disabled) el.setAttribute("tabindex", "-1")
    else el.removeAttribute("tabindex")
  })
}

function setToggleState(mode) {
  document.querySelectorAll("[data-job-view-toggle]").forEach((btn) => {
    const active = btn.dataset.jobViewToggle === mode
    btn.classList.toggle("is-active", active)
    btn.setAttribute("aria-pressed", String(active))
  })
}

function hidePagination(hide) {
  const pag = document.querySelector(".modern-pagination-wrapper")
  const loadMore = document.querySelector(".modern-jobs-load-more")
  if (pag) pag.hidden = hide
  if (loadMore) loadMore.hidden = hide
}

async function ensureGlobe(r, payload) {
  if (r._globe) {
    r._globe.resume?.()
    r._globe.resize?.()
    return r._globe
  }
  if (r._globePromise) return r._globePromise

  const filters = new JobWorldFilters(payload)
  r._filters = filters

  r._globePromise = import(/* webpackChunkName: "job-world-globe" */ "./job_world_globe.js")
    .then(async ({ initJobWorldGlobe }) => {
      const panel = r.querySelector('[data-job-view-panel="world"]')
      const globe = await initJobWorldGlobe(panel, {
        onMarkerClick: (job) => showActiveCard(panel, job)
      })
      if (!globe) return null
      r._globe = globe
      globe.setJobs(filters.apply())
      filters.on("filterschange", (jobs) => {
        globe.setJobs(jobs)
        renderRailList(panel, jobs)
        updateCount(panel, jobs.length)
      })
      wireRail(panel, filters)
      renderRailList(panel, filters.apply())
      updateCount(panel, filters.apply().length)
      wireResize(r)
      return globe
    })
    .catch((err) => {
      console.error("Failed to load globe:", err)
      r._globePromise = null
      return null
    })

  return r._globePromise
}

function showActiveCard(panel, job) {
  const el = panel.querySelector("[data-job-world-active-card]")
  if (!el || !job) return
  el.hidden = false
  el.innerHTML = `
    <div class="job-world-view__active-card-inner">
      <div class="job-world-view__active-card-title">${escapeHtml(job.title)}</div>
      <div class="job-world-view__active-card-meta">${escapeHtml(job.company || "")} · ${escapeHtml(job.location || "")}</div>
      ${job.salary ? `<div class="job-world-view__active-card-salary">${escapeHtml(String(job.salary))}</div>` : ""}
      <a class="job-world-view__active-card-cta" href="${job.url}">View role →</a>
    </div>
  `
}

// Initial number of jobs visible in the rail. The "Show more" button reveals
// the rest in chunks so the page doesn't grow taller than the viewport.
const RAIL_INITIAL_COUNT = 10
const RAIL_PAGE_SIZE = 20

function renderRailList(panel, jobs) {
  const list = panel.querySelector("[data-job-world-list]")
  if (!list) return
  // Cache the latest job set on the list element. Filter changes overwrite it
  // and reset the visible count, so users always see the freshest top of the
  // list when they re-filter.
  list._jobs = jobs
  list._visible = Math.min(RAIL_INITIAL_COUNT, jobs.length)
  paintRailList(panel)
}

function paintRailList(panel) {
  const list = panel.querySelector("[data-job-world-list]")
  if (!list) return
  const jobs = list._jobs || []
  const visible = list._visible || 0
  const items = jobs.slice(0, visible).map((j) => `
    <a class="job-world-view__rail-item" role="option" href="${j.url}">
      <span class="job-world-view__rail-item-title">${escapeHtml(j.title)}</span>
      <span class="job-world-view__rail-item-meta">${escapeHtml(j.company || "")} · ${escapeHtml(j.location || "")}</span>
    </a>
  `).join("")
  const remaining = Math.max(0, jobs.length - visible)
  const moreBtn = remaining > 0
    ? `<button type="button" class="job-world-view__rail-more" data-job-world-rail-more aria-label="Show more roles">
         Show ${Math.min(remaining, RAIL_PAGE_SIZE)} more
         <span class="job-world-view__rail-more-count">${remaining} hidden</span>
       </button>`
    : ""
  list.innerHTML = items + moreBtn
  const btn = list.querySelector("[data-job-world-rail-more]")
  if (btn) {
    btn.addEventListener("click", () => {
      list._visible = Math.min(jobs.length, (list._visible || 0) + RAIL_PAGE_SIZE)
      paintRailList(panel)
    })
  }
}

function updateCount(panel, n) {
  const el = panel.querySelector("[data-job-world-count]")
  if (el) el.textContent = `${n.toLocaleString()} mappable roles`
}

function wireRail(panel, filters) {
  const search = panel.querySelector("[data-job-world-search]")
  if (search) {
    search.addEventListener("input", (e) => filters.setSearch(e.target.value))
  }
  panel.querySelectorAll("[data-job-world-region]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const region = btn.dataset.jobWorldRegion
      panel.querySelectorAll("[data-job-world-region]").forEach((b) => {
        b.classList.toggle("is-active", b === btn)
        b.setAttribute("aria-pressed", String(b === btn))
      })
      filters.setRegion(region)
      panel.closest("[data-job-world-view-root]")._globe?.focusRegion(region)
    })
  })
  panel.querySelectorAll("[data-job-world-date-range]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const days = btn.dataset.jobWorldDateRange
      panel.querySelectorAll("[data-job-world-date-range]").forEach((b) => {
        b.classList.toggle("is-active", b === btn)
        b.setAttribute("aria-pressed", String(b === btn))
      })
      filters.setDateRange(days)
    })
  })
  const zoomIn = panel.querySelector('[data-job-world-zoom="in"]')
  const zoomOut = panel.querySelector('[data-job-world-zoom="out"]')
  if (zoomIn) zoomIn.addEventListener("click", () => {
    const globe = panel.closest("[data-job-world-view-root]")._globe
    if (globe) globe.camera.position.z = Math.min(12, globe.camera.position.z + 1)
  })
  if (zoomOut) zoomOut.addEventListener("click", () => {
    const globe = panel.closest("[data-job-world-view-root]")._globe
    if (globe) globe.camera.position.z = Math.max(2, globe.camera.position.z - 1)
  })

  renderTagChips(panel, filters)
}

function renderTagChips(panel, filters) {
  const group = panel.querySelector("[data-job-world-tag-group]")
  if (!group) return
  const counts = new Map()
  filters.jobs.forEach((j) => (j.tags || []).forEach((t) => counts.set(t, (counts.get(t) || 0) + 1)))
  const top = Array.from(counts.entries()).sort((a, b) => b[1] - a[1]).slice(0, 8)
  const legend = group.querySelector("legend")
  group.innerHTML = ""
  if (legend) group.appendChild(legend)
  top.forEach(([tag, count]) => {
    const btn = document.createElement("button")
    btn.type = "button"
    btn.className = "job-world-view__chip"
    btn.dataset.jobWorldTag = tag
    btn.setAttribute("aria-pressed", "false")
    // Split label + count into separate spans so the active-state count can
    // be colored with the same lime accent the top filter pills use.
    btn.innerHTML = `${escapeHtml(formatTagLabel(tag))}<span class="job-world-view__chip-count">${count.toLocaleString()}</span>`
    btn.addEventListener("click", () => {
      const active = btn.classList.toggle("is-active")
      group.querySelectorAll("[data-job-world-tag]").forEach((b) => {
        if (b !== btn) {
          b.classList.remove("is-active")
          b.setAttribute("aria-pressed", "false")
        }
      })
      btn.setAttribute("aria-pressed", String(active))
      filters.setTag(active ? tag : null)
    })
    group.appendChild(btn)
  })
}

function wireResize(r) {
  if (r._resizeBound) return
  let raf = null
  window.addEventListener("resize", () => {
    if (raf) cancelAnimationFrame(raf)
    raf = requestAnimationFrame(() => {
      if (!isDesktop() && r.classList.contains("is-world-view")) {
        setMode("list")
      } else {
        r._globe?.resize()
      }
    })
  })
  r._resizeBound = true
}

function escapeHtml(s) {
  return String(s || "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]))
}

const TAG_LABEL_EXACT = new Map([
  ["ai", "AI"],
  ["llm", "LLM"],
  ["nlp", "NLP"],
  ["mlops", "MLOps"],
  ["pytorch", "PyTorch"],
  ["tensorflow", "TensorFlow"],
  ["openai", "OpenAI"],
  ["javascript", "JavaScript"],
  ["typescript", "TypeScript"],
  ["aws", "AWS"],
  ["gcp", "GCP"],
  ["scikitlearn", "Scikit-Learn"],
  ["scikit learn", "Scikit-Learn"],
  ["langchain", "LangChain"],
  ["fulltime", "Full Time"],
  ["full time", "Full Time"],
  ["parttime", "Part Time"],
  ["part time", "Part Time"],
  ["fullstack", "Full Stack"],
  ["full stack", "Full Stack"],
  ["nontech", "Non-Tech"],
  ["non tech", "Non-Tech"],
  ["entrylevel", "Entry Level"],
  ["entry level", "Entry Level"],
  ["dataengineer", "Data Engineer"],
  ["data engineer", "Data Engineer"],
  ["computervision", "Computer Vision"],
  ["computer vision", "Computer Vision"],
  ["datascience", "Data Science"],
  ["data science", "Data Science"],
  ["machinelearning", "Machine Learning"],
  ["machine learning", "Machine Learning"],
  ["artificialintelligence", "Artificial Intelligence"],
  ["artificial intelligence", "Artificial Intelligence"]
])

const TAG_WORD_OVERRIDES = new Map([
  ["ai", "AI"],
  ["llm", "LLM"],
  ["nlp", "NLP"],
  ["mlops", "MLOps"],
  ["aws", "AWS"],
  ["gcp", "GCP"],
  ["ui", "UI"],
  ["ux", "UX"],
  ["qa", "QA"],
  ["api", "API"],
  ["sql", "SQL"],
  ["gpu", "GPU"],
  ["ml", "ML"],
  ["r", "R"]
])

export function formatTagLabel(tag) {
  const raw = String(tag || "").trim()
  if (!raw) return ""

  const normalized = raw.replace(/[_-]+/g, " ").replace(/\s+/g, " ").trim().toLowerCase()
  const compact = normalized.replace(/\s+/g, "")

  if (TAG_LABEL_EXACT.has(normalized)) return TAG_LABEL_EXACT.get(normalized)
  if (TAG_LABEL_EXACT.has(compact)) return TAG_LABEL_EXACT.get(compact)

  return normalized
    .split(" ")
    .map((word) => TAG_WORD_OVERRIDES.get(word) || (word.charAt(0).toUpperCase() + word.slice(1)))
    .join(" ")
}

export function setMode(mode) {
  const r = root()
  if (!r) return
  const next = mode === "world" ? "world" : "list"
  if (next === "world" && (!isDesktop() || !hasWebGL())) {
    setToggleState("list")
    return
  }

  const listPanel = r.querySelector('[data-job-view-panel="list"]')
  const worldPanel = r.querySelector('[data-job-view-panel="world"]')
  const isWorld = next === "world"

  r.classList.toggle("is-world-view", isWorld)
  if (listPanel) listPanel.hidden = isWorld
  if (worldPanel) worldPanel.hidden = !isWorld
  hidePagination(isWorld)
  disableTopFilters(isWorld)
  setToggleState(next)
  storeMode(next)

  if (isWorld) {
    ensureGlobe(r, readPayload(worldPanel))
  } else {
    r._globe?.pause?.()
  }
}

function onToggleClick(event) {
  const btn = event.target.closest("[data-job-view-toggle]")
  if (!btn) return
  event.preventDefault()
  setMode(btn.dataset.jobViewToggle)
}

function onBeforeCache() {
  document.querySelectorAll("[data-job-world-view-root]").forEach((r) => {
    r._globe?.dispose?.()
    delete r._globe
    delete r._globePromise
    delete r._filters
  })
}

export function bindJobWorldView() {
  const r = root()
  if (!r) return

  if (!bound) {
    document.addEventListener("click", onToggleClick)
    bound = true
  }
  if (!cacheBound) {
    document.addEventListener("turbo:before-cache", onBeforeCache)
    cacheBound = true
  }

  const initial = storedMode()
  if (initial === "world" && isHomepageRoute() && isDesktop() && hasWebGL()) {
    setMode("world")
  } else {
    setMode("list")
  }
}
